import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  AddCircleOutline as RequestIcon,
  PlayCircleOutline as ActiveIcon,
  History as HistoryIcon,
  Approval as ApprovalIcon,
  AccessTime as TimeIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  HourglassEmpty as PendingIcon,
} from '@mui/icons-material';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../animations/variants';
import { getMyRequests, getActiveSessions, getPendingApprovals, getJITUser } from '../../services/jitApi';
import { hasMinRole, ROLES } from '../../utils/roles';

// Helper function to derive display name from email
const getDisplayNameFromEmail = (email) => {
  if (!email) return 'User';
  // Extract the part before @ symbol
  const localPart = email.split('@')[0];
  // Replace dots, underscores, and hyphens with spaces, then capitalize each word
  return localPart
    .replace(/[._-]/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const JITDashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const user = getJITUser();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    activeSessions: 0,
    pendingRequests: 0,
    pendingApprovals: 0,
    totalRequests: 0,
  });
  const [recentRequests, setRecentRequests] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [requestsRes, sessionsRes] = await Promise.all([
        getMyRequests(),
        getActiveSessions(),
      ]);

      const requests = requestsRes.data?.requests || [];
      const totalRequests = requestsRes.data?.total || requests.length;
      const sessions = sessionsRes.data || [];

      // Calculate stats
      const pendingRequestsCount = requests.filter(r => r.status === 'pending').length;

      setStats({
        activeSessions: sessions.length,
        pendingRequests: pendingRequestsCount,
        pendingApprovals: 0,
        totalRequests: totalRequests,
      });

      setRecentRequests(requests.slice(0, 5));
      setActiveSessions(sessions.slice(0, 3));

      // Fetch pending approvals if user is approver
      if (hasMinRole(user?.role, ROLES.APPROVER)) {
        try {
          const approvalsRes = await getPendingApprovals();
          setStats(prev => ({
            ...prev,
            pendingApprovals: (approvalsRes.data || []).length,
          }));
        } catch (err) {
          console.error('Failed to fetch pending approvals:', err);
        }
      }
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      pending: { color: 'warning', icon: <PendingIcon sx={{ fontSize: 14 }} /> },
      approved: { color: 'success', icon: <ApprovedIcon sx={{ fontSize: 14 }} /> },
      rejected: { color: 'error', icon: <RejectedIcon sx={{ fontSize: 14 }} /> },
      expired: { color: 'default', icon: <TimeIcon sx={{ fontSize: 14 }} /> },
      revoked: { color: 'error', icon: <RejectedIcon sx={{ fontSize: 14 }} /> },
      active: { color: 'success', icon: <ActiveIcon sx={{ fontSize: 14 }} /> },
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <Chip
        size="small"
        label={status.charAt(0).toUpperCase() + status.slice(1)}
        color={config.color}
        icon={config.icon}
        sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}
      />
    );
  };

  const formatTimeRemaining = (expiresAt) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires - now;

    if (diff <= 0) return 'Expired';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours}h ${minutes}m remaining`;
    }
    return `${minutes}m remaining`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: '#4698D3' }} />
      </Box>
    );
  }

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
    >
      {/* Header */}
      <motion.div variants={sectionVariants}>
      <Box sx={{ mb: { xs: 3, sm: 4 } }}>
        <Typography
          variant="h4"
          sx={{
            fontFamily: 'Bitter, Georgia, serif',
            fontWeight: 600,
            color: '#253E66',
            mb: 1,
            fontSize: { xs: '1.5rem', sm: '2.125rem' },
          }}
        >
          Welcome back{user?.email ? `, ${getDisplayNameFromEmail(user.email)}` : ''}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: '#666666',
            fontFamily: 'Metropolis, sans-serif',
          }}
        >
          Request and manage your Just-In-Time access to AWS resources
        </Typography>
      </Box>

      </motion.div>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <motion.div variants={sectionVariants} style={{ marginBottom: 32 }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: { xs: 1.5, sm: 3 },
        }}
      >
        {/* Active Sessions */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 3 },
            border: '1px solid #E5E5E5',
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            flex: { xs: '0 0 calc(50% - 6px)', sm: '0 0 calc(50% - 12px)', md: '1 1 0' },
            minWidth: 0,
            '&:hover': {
              borderColor: '#4698D3',
              boxShadow: '0 4px 12px rgba(70, 152, 211, 0.15)',
            },
          }}
          onClick={() => navigate('/active-sessions')}
        >
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
            <Box
              sx={{
                width: { xs: 36, sm: 48 },
                height: { xs: 36, sm: 48 },
                borderRadius: '8px',
                backgroundColor: 'rgba(39, 174, 96, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <ActiveIcon sx={{ color: '#27AE60', fontSize: { xs: 18, sm: 24 } }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 700,
                  color: '#253E66',
                  fontSize: { xs: '1.25rem', sm: '2.125rem' },
                  textAlign: { xs: 'center', sm: 'left' },
                }}
              >
                {stats.activeSessions}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#666666',
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  textAlign: { xs: 'center', sm: 'left' },
                }}
              >
                Active Sessions
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Pending Requests */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 3 },
            border: '1px solid #E5E5E5',
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            flex: { xs: '0 0 calc(50% - 6px)', sm: '0 0 calc(50% - 12px)', md: '1 1 0' },
            minWidth: 0,
            '&:hover': {
              borderColor: '#F39C12',
              boxShadow: '0 4px 12px rgba(243, 156, 18, 0.15)',
            },
          }}
          onClick={() => navigate('/my-requests')}
        >
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
            <Box
              sx={{
                width: { xs: 36, sm: 48 },
                height: { xs: 36, sm: 48 },
                borderRadius: '8px',
                backgroundColor: 'rgba(243, 156, 18, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <PendingIcon sx={{ color: '#F39C12', fontSize: { xs: 18, sm: 24 } }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 700,
                  color: '#253E66',
                  fontSize: { xs: '1.25rem', sm: '2.125rem' },
                  textAlign: { xs: 'center', sm: 'left' },
                }}
              >
                {stats.pendingRequests}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#666666',
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  textAlign: { xs: 'center', sm: 'left' },
                }}
              >
                Pending Requests
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Pending Approvals (approver only) */}
        {hasMinRole(user?.role, ROLES.APPROVER) && (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.5, sm: 3 },
              border: '1px solid #E5E5E5',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              flex: { xs: '0 0 calc(50% - 6px)', sm: '0 0 calc(50% - 12px)', md: '1 1 0' },
              minWidth: 0,
              '&:hover': {
                borderColor: '#D64794',
                boxShadow: '0 4px 12px rgba(214, 71, 148, 0.15)',
              },
            }}
            onClick={() => navigate('/approvals')}
          >
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
              <Box
                sx={{
                  width: { xs: 36, sm: 48 },
                  height: { xs: 36, sm: 48 },
                  borderRadius: '8px',
                  backgroundColor: 'rgba(214, 71, 148, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <ApprovalIcon sx={{ color: '#D64794', fontSize: { xs: 18, sm: 24 } }} />
              </Box>
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 700,
                    color: '#253E66',
                    fontSize: { xs: '1.25rem', sm: '2.125rem' },
                    textAlign: { xs: 'center', sm: 'left' },
                  }}
                >
                  {stats.pendingApprovals}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#666666',
                    fontFamily: 'Metropolis, sans-serif',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    textAlign: { xs: 'center', sm: 'left' },
                  }}
                >
                  Pending Approvals
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Total Requests */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 3 },
            border: '1px solid #E5E5E5',
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            flex: { xs: '0 0 calc(50% - 6px)', sm: '0 0 calc(50% - 12px)', md: '1 1 0' },
            minWidth: 0,
            '&:hover': {
              borderColor: '#4698D3',
              boxShadow: '0 4px 12px rgba(70, 152, 211, 0.15)',
            },
          }}
          onClick={() => navigate('/my-requests')}
        >
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
            <Box
              sx={{
                width: { xs: 36, sm: 48 },
                height: { xs: 36, sm: 48 },
                borderRadius: '8px',
                backgroundColor: 'rgba(70, 152, 211, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <HistoryIcon sx={{ color: '#4698D3', fontSize: { xs: 18, sm: 24 } }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 700,
                  color: '#253E66',
                  fontSize: { xs: '1.25rem', sm: '2.125rem' },
                  textAlign: { xs: 'center', sm: 'left' },
                }}
              >
                {stats.totalRequests}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#666666',
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  textAlign: { xs: 'center', sm: 'left' },
                }}
              >
                Total Requests
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
      </motion.div>

      {/* Quick Actions */}
      <motion.div variants={sectionVariants} style={{ marginBottom: 32 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: '1px solid #E5E5E5',
          borderRadius: '8px',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: 'Metropolis, sans-serif',
            fontWeight: 600,
            color: '#253E66',
            mb: 2,
          }}
        >
          Quick Actions
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<RequestIcon />}
            onClick={() => navigate('/request')}
            sx={{
              backgroundColor: '#4698D3',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#3580B8',
              },
            }}
          >
            Request Access
          </Button>
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={() => navigate('/my-requests')}
            sx={{
              borderColor: '#E5E5E5',
              color: '#253E66',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                borderColor: '#4698D3',
                backgroundColor: 'rgba(70, 152, 211, 0.04)',
              },
            }}
          >
            View My Requests
          </Button>
          {hasMinRole(user?.role, ROLES.APPROVER) && (
            <Button
              variant="outlined"
              startIcon={<ApprovalIcon />}
              onClick={() => navigate('/approvals')}
              sx={{
                borderColor: '#D64794',
                color: '#D64794',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#B83A7D',
                  backgroundColor: 'rgba(214, 71, 148, 0.04)',
                },
              }}
            >
              Review Approvals
            </Button>
          )}
        </Box>
      </Paper>
      </motion.div>

      {/* Active Sessions */}
      {activeSessions.length > 0 && (
        <motion.div variants={sectionVariants} style={{ marginBottom: 32 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: '1px solid #E5E5E5',
            borderRadius: '8px',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                color: '#253E66',
              }}
            >
              Active Sessions
            </Typography>
            <Button
              size="small"
              onClick={() => navigate('/active-sessions')}
              sx={{
                color: '#4698D3',
                fontFamily: 'Metropolis, sans-serif',
                textTransform: 'none',
              }}
            >
              View All
            </Button>
          </Box>
          <Grid container spacing={2}>
            {activeSessions.map((session) => (
              <Grid item xs={12} md={4} key={session.id}>
                <Card
                  variant="outlined"
                  sx={{
                    borderColor: '#27AE60',
                    borderRadius: '8px',
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontFamily: 'Metropolis, sans-serif',
                          fontWeight: 600,
                          color: '#253E66',
                        }}
                      >
                        {session.accountName || session.accountId}
                      </Typography>
                      {getStatusChip('active')}
                    </Box>
                    {session.accountName && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#888888',
                          fontFamily: 'Metropolis, sans-serif',
                          fontSize: '11px',
                        }}
                      >
                        {session.accountId}
                      </Typography>
                    )}
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#666666',
                        fontFamily: 'Metropolis, sans-serif',
                        mb: 1,
                      }}
                    >
                      {session.permissionSetName || session.permissionSetId}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#27AE60',
                        fontFamily: 'Metropolis, sans-serif',
                        fontWeight: 500,
                      }}
                    >
                      {formatTimeRemaining(session.expiresAt)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
        </motion.div>
      )}

      {/* Recent Requests */}
      {recentRequests.length > 0 && (
        <motion.div variants={sectionVariants}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: '1px solid #E5E5E5',
            borderRadius: '8px',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                color: '#253E66',
              }}
            >
              Recent Requests
            </Typography>
            <Button
              size="small"
              onClick={() => navigate('/my-requests')}
              sx={{
                color: '#4698D3',
                fontFamily: 'Metropolis, sans-serif',
                textTransform: 'none',
              }}
            >
              View All
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {recentRequests.map((request, index) => (
              <Box
                key={request.id}
                sx={{
                  '@keyframes fadeInItem': {
                    from: { opacity: 0, transform: 'translateY(8px)' },
                    to: { opacity: 1, transform: 'translateY(0)' },
                  },
                  animation: `fadeInItem 0.3s ease-out ${index * 0.05}s both`,
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: { xs: 1, sm: 0 },
                  p: 2,
                  borderRadius: '6px',
                  backgroundColor: '#F9FBFD',
                  border: '1px solid #E5E5E5',
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="body2"
                    noWrap={isMobile}
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 600,
                      color: '#253E66',
                    }}
                  >
                    {request.accountName || request.accountId}
                    {request.accountName && !isMobile && (
                      <Typography
                        component="span"
                        sx={{
                          color: '#888888',
                          fontFamily: 'Metropolis, sans-serif',
                          fontSize: '12px',
                          ml: 1,
                        }}
                      >
                        ({request.accountId})
                      </Typography>
                    )}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#666666',
                      fontFamily: 'Metropolis, sans-serif',
                    }}
                  >
                    {request.permissionSetName || request.permissionSetId} • {request.requestedDuration}h
                  </Typography>
                </Box>
                {getStatusChip(request.status)}
              </Box>
            ))}
          </Box>
        </Paper>
        </motion.div>
      )}
    </motion.div>
  );
};

export default JITDashboard;
